// =============================================================================
// ACTION STRIP - Контекстний банер (Tactical HUD)
// =============================================================================

import { motion, AnimatePresence } from 'framer-motion';
import { useCallback, useEffect } from 'react';
import type { DonationCampaign } from '../types';
import { trackActionStripClick, trackActionStripDismiss } from '../services/analytics';

interface ActionStripProps {
  isVisible: boolean;
  campaign: DonationCampaign;
  decisionNumber: number;
  onDismiss: () => void;
}

export function ActionStrip({
  isVisible,
  campaign,
  decisionNumber,
  onDismiss,
}: ActionStripProps) {
  const campaignType = campaign.type === 'petition' ? 'petition' : 'donation';

  const handleDonate = useCallback(() => {
    trackActionStripClick(campaignType);
    window.open(campaign.url, '_blank');
    onDismiss();
  }, [campaign.url, campaignType, onDismiss]);

  const handleDismiss = useCallback(() => {
    trackActionStripDismiss(decisionNumber);
    onDismiss();
  }, [decisionNumber, onDismiss]);

  // Wire ESC key support
  useEffect(() => {
    if (!isVisible) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, handleDismiss]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-0 left-0 right-0 z-[100] px-4 pointer-events-none"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="max-w-4xl mx-auto pointer-events-auto overflow-hidden shadow-[0_-20px_50px_rgba(0,0,0,0.5)]">
            {/* Top accent bar */}
            <div className="h-1 bg-war-amber animate-pulse" />

            <div className="bg-war-panel/95 backdrop-blur-xl border-x border-b border-war-amber/30 p-4 md:p-6 flex flex-col md:flex-row items-center gap-4 md:gap-8 justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 border-2 border-war-amber flex items-center justify-center text-war-amber animate-pulse">
                  <span className="font-mono text-xl md:text-2xl font-black">
                    !
                  </span>
                </div>
                <div className="space-y-1">
                  <h3 className="text-war-amber font-mono text-[10px] md:text-xs font-bold uppercase tracking-[.25em]">
                    {campaign.type === 'petition'
                      ? 'ПЕРЕРИВАННЯ СИМУЛЯЦІЇ // РЕАЛЬНА ПЕТИЦІЯ'
                      : 'ПЕРЕРИВАННЯ СИМУЛЯЦІЇ // РЕАЛЬНА ЦІЛЬ'}
                  </h3>
                  <p className="text-war-bright font-mono text-xs md:text-sm uppercase leading-tight">
                    {campaign.type === 'petition' ? (
                      <>
                        Тут — симуляція. За вікном — справжня війна.{' '}
                        <span className="text-war-dim">
                          Підпишіть петицію, щоб ваш голос почули.
                        </span>
                      </>
                    ) : (
                      <>
                        Тут — симуляція. За вікном — справжня війна.{' '}
                        <span className="text-war-dim">
                          Підтримайте тих, хто тримає небо.
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                <button
                  onClick={handleDismiss}
                  aria-label="Ігнорувати та закрити банер"
                  className="flex-1 md:px-6 py-3 min-h-[44px] font-mono text-[10px] text-war-dim uppercase tracking-wider hover:text-war-bright transition-colors"
                >
                  ІГНОРУВАТИ [ESC]
                </button>
                <button
                  onClick={handleDonate}
                  aria-label={campaign.type === 'petition' ? 'Перейти на сторінку петиції' : 'Відкрити сторінку підтримки Збройних Сил України'}
                  className="flex-[2] md:px-8 py-3 min-h-[44px] bg-war-amber text-black font-mono font-bold uppercase tracking-wider text-[11px] md:text-sm hover:bg-war-amber/90 transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)] active:scale-95"
                >
                  {campaign.type === 'petition' ? 'ПІДПИСАТИ ПЕТИЦІЮ' : 'ПІДТРИМАТИ ЗСУ'}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
